// Construction-time normalization and validation for the expressCheckout config.
// Call normalizeExpressCheckoutConfig first, then validateExpressCheckoutConfig.

export const VALID_MODES = ['one_time', 'autopay', 'tokenization'];

const ALLOWED_FREQUENCIES = [
  'onetime', 'weekly', 'every2weeks', 'monthly',
  'every3months', 'every6months', 'annually',
];

// Returns a normalized expressCheckout config object.
// If mode is absent, defaults to "one_time".
export function normalizeExpressCheckoutConfig(expressCheckout = {}) {
  if (expressCheckout.mode) return expressCheckout;
  return { ...expressCheckout, mode: 'one_time' };
}

// Validates the normalized expressCheckout config. Throws on any violation.
// Must be called after normalizeExpressCheckoutConfig.
export function validateExpressCheckoutConfig(expressCheckout = {}) {
  _validateMode(expressCheckout);
  _validateRequiredFields(expressCheckout);
  _validateOptionalFields(expressCheckout);
  _validateSplitFunding(expressCheckout);
  _validateWallets(expressCheckout);
  _validateAutopay(expressCheckout);
  _validateInvoiceData(expressCheckout);
  _validateTokenization(expressCheckout);
}

// ─── private helpers ──────────────────────────────────────────────────────────

function _validateMode({ mode }) {
  if (!mode || !VALID_MODES.includes(mode)) {
    throw new Error(
      `Invalid mode '${mode}'. Must be one of: ${VALID_MODES.join(', ')}`
    );
  }
}

function _validateRequiredFields(ec) {
  const required = [
    { key: 'amount', type: 'number' },
    { key: 'fee', type: 'number' },
    { key: 'currency', type: 'string' },
    { key: 'supportedNetworks', type: 'object' },
  ];

  for (const { key, type } of required) {
    if (!(key in ec)) {
      throw new Error(`Missing required field: ${key}`);
    }
    if (typeof ec[key] !== type) {
      throw new Error(`Field ${key} must be of type ${type}`);
    }
  }

  if (!Array.isArray(ec.supportedNetworks)) {
    throw new Error('Field supportedNetworks must be an array');
  }
}

function _validateOptionalFields(ec) {
  if ('saveIfSuccess' in ec && typeof ec.saveIfSuccess !== 'boolean') {
    throw new Error('Field saveIfSuccess must be of type boolean');
  }

  if (ec.layout && 'columns' in ec.layout && typeof ec.layout.columns !== 'number') {
    throw new Error('Field columns must be of type number');
  }

  if (ec.appearance) {
    const a = ec.appearance;
    if ('buttonHeight' in a && typeof a.buttonHeight !== 'number') {
      throw new Error('Field buttonHeight must be of type number');
    }
    if ('buttonBorderRadius' in a && typeof a.buttonBorderRadius !== 'number') {
      throw new Error('Field buttonBorderRadius must be of type number');
    }
    if (a.padding) {
      if ('x' in a.padding && typeof a.padding.x !== 'number') {
        throw new Error('Field x must be of type number');
      }
      if ('y' in a.padding && typeof a.padding.y !== 'number') {
        throw new Error('Field y must be of type number');
      }
    }
  }

  if (ec.applePay) {
    const ap = ec.applePay;
    if ('buttonStyle' in ap && typeof ap.buttonStyle !== 'string') {
      throw new Error('Field buttonStyle must be of type string');
    }
    if ('buttonType' in ap && typeof ap.buttonType !== 'string') {
      throw new Error('Field buttonType must be of type string');
    }
    if ('language' in ap && typeof ap.language !== 'string') {
      throw new Error('Field language must be of type string');
    }
  }
}

function _validateSplitFunding(ec) {
  if (!('splitFunding' in ec)) return;

  if (!Array.isArray(ec.splitFunding)) {
    throw new Error('Field splitFunding must be an array');
  }

  ec.splitFunding.forEach((item, index) => {
    if (!item || typeof item !== 'object') {
      throw new Error(`splitFunding[${index}] must be an object`);
    }
    if (
      !('recipientEntryPoint' in item) ||
      typeof item.recipientEntryPoint !== 'string' ||
      !item.recipientEntryPoint.trim()
    ) {
      throw new Error(
        `splitFunding[${index}].recipientEntryPoint is required and must be a non-empty string`
      );
    }
    if (!('amount' in item) || typeof item.amount !== 'number') {
      throw new Error(`splitFunding[${index}].amount is required and must be a number`);
    }
    if ('accountId' in item && typeof item.accountId !== 'string') {
      throw new Error(`splitFunding[${index}].accountId must be a string`);
    }
    if ('description' in item && typeof item.description !== 'string') {
      throw new Error(`splitFunding[${index}].description must be a string`);
    }
  });
}

function _validateWallets(ec) {
  if (ec.applePay) {
    if (!('enabled' in ec.applePay)) {
      throw new Error('Missing required field: enabled');
    }
    if (typeof ec.applePay.enabled !== 'boolean') {
      throw new Error('Field enabled must be of type boolean');
    }
  }
  if (ec.googlePay) {
    if (!('enabled' in ec.googlePay)) {
      throw new Error('Missing required field: enabled');
    }
    if (typeof ec.googlePay.enabled !== 'boolean') {
      throw new Error('Field enabled must be of type boolean');
    }
  }
}

function _validateAutopay(ec) {
  if (ec.mode !== 'autopay') return;

  const { autopay } = ec;
  if (!autopay) {
    throw new Error("autopay config block is required when mode is 'autopay'");
  }
  if (!autopay || typeof autopay !== 'object' || Array.isArray(autopay)) {
    throw new Error('autopay must be an object');
  }
  if (!autopay.frequency || !ALLOWED_FREQUENCIES.includes(autopay.frequency)) {
    throw new Error(
      `autopay.frequency must be one of: ${ALLOWED_FREQUENCIES.join(', ')}`
    );
  }
  if (!autopay.startDate) {
    throw new Error("autopay.startDate is required when mode is 'autopay'");
  }
  const start = new Date(autopay.startDate);
  if (isNaN(start.getTime())) {
    throw new Error('autopay.startDate must be a valid date');
  }
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  if (start < tomorrow) {
    throw new Error('autopay.startDate must be at least 1 day in the future');
  }
  if ('endDate' in autopay && typeof autopay.endDate !== 'string') {
    throw new Error('autopay.endDate must be of type string');
  }
  if ('untilCancel' in autopay && typeof autopay.untilCancel !== 'boolean') {
    throw new Error('autopay.untilCancel must be of type boolean');
  }
}

function _validateTokenization(ec) {
  if (ec.mode !== 'tokenization') return;

  if (ec.autopay) {
    throw new Error('tokenization mode is incompatible with an autopay config block');
  }
  if ('invoiceData' in ec) {
    throw new Error('invoiceData is not supported in tokenization mode');
  }
  if (ec.amount !== undefined && Number(ec.amount) > 0) {
    console.warn(
      '[Payabli] amount is ignored in tokenization mode — a $0.01 authorization will be used for card validation'
    );
  }
  if ('fallbackAuthAmount' in ec && typeof ec.fallbackAuthAmount !== 'number') {
    throw new Error('Field fallbackAuthAmount must be of type number');
  }
  if ('methodDescription' in ec && typeof ec.methodDescription !== 'string') {
    throw new Error('Field methodDescription must be of type string');
  }
}

// ─── invoiceData (BillData) schema ────────────────────────────────────────────

const _INVOICE_DATA_STRING_FIELDS = [
  'invoiceNumber', 'invoiceDate', 'invoiceDueDate', 'invoiceEndDate',
  'purchaseOrder', 'summaryCommodityCode', 'termsConditions', 'notes',
  'paymentTerms', 'frequency',
  'company', 'firstName', 'lastName',
  'shippingAddress1', 'shippingAddress2', 'shippingCity', 'shippingState',
  'shippingZip', 'shippingCountry', 'shippingEmail', 'shippingPhone', 'shippingFromZip',
];
const _INVOICE_DATA_NUMBER_FIELDS = [
  'invoiceAmount', 'tax', 'discount', 'freightAmount', 'dutyAmount',
  'invoiceStatus', 'invoiceType',
];
const _INVOICE_DATA_ARRAY_FIELDS  = ['items', 'attachments'];
const _INVOICE_DATA_OBJECT_FIELDS = ['AdditionalData'];

const _INVOICE_DATA_KNOWN_FIELDS = [
  ..._INVOICE_DATA_STRING_FIELDS,
  ..._INVOICE_DATA_NUMBER_FIELDS,
  ..._INVOICE_DATA_ARRAY_FIELDS,
  ..._INVOICE_DATA_OBJECT_FIELDS,
];

function _isPlainObject(v) {
  return (
    v !== null && typeof v === 'object' && !Array.isArray(v) &&
    (Object.getPrototypeOf(v) === Object.prototype ||
     Object.getPrototypeOf(v) === null)
  );
}

function _validateInvoiceData(ec) {
  if (!('invoiceData' in ec)) return;
  const { invoiceData } = ec;

  if (!_isPlainObject(invoiceData)) {
    throw new Error('invoiceData must be a plain object');
  }

  for (const key of Object.keys(invoiceData)) {
    if (!_INVOICE_DATA_KNOWN_FIELDS.includes(key)) {
      throw new Error(`invoiceData: unknown field '${key}'`);
    }
  }

  for (const key of _INVOICE_DATA_STRING_FIELDS) {
    if (key in invoiceData && invoiceData[key] !== null &&
        typeof invoiceData[key] !== 'string') {
      throw new Error(`invoiceData.${key} must be of type string`);
    }
  }

  for (const key of _INVOICE_DATA_NUMBER_FIELDS) {
    if (key in invoiceData && invoiceData[key] !== null &&
        typeof invoiceData[key] !== 'number') {
      throw new Error(`invoiceData.${key} must be of type number`);
    }
  }

  for (const key of _INVOICE_DATA_ARRAY_FIELDS) {
    if (key in invoiceData && invoiceData[key] !== null &&
        !Array.isArray(invoiceData[key])) {
      throw new Error(`invoiceData.${key} must be an array`);
    }
  }

  for (const key of _INVOICE_DATA_OBJECT_FIELDS) {
    if (key in invoiceData && invoiceData[key] !== null &&
        !_isPlainObject(invoiceData[key])) {
      throw new Error(`invoiceData.${key} must be a plain object`);
    }
  }
}
